import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppState } from '@store/app.state';
import { ToolActions } from '@store/actions/tool.actions';
import { selectActiveTool, selectActiveColor, selectRecentColors, selectBrushSize, selectSymmetryMode, selectShapeFillMode } from '@store/selectors/tool.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { ToolType, SymmetryMode, ShapeFillMode } from '@models/tool.model';
import { BUILTIN_PALETTES } from '@models/palette.model';
import { CollaborationService } from '@app/services/collaboration.service';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';

const i = (d: string) =>
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

// Raw SVG strings - converted to SafeHtml in constructor
const RAW_DRAW_TOOLS: { type: ToolType; label: string; desc: string; shortcut: string; svg: string }[] = [
  { type: 'draw',   label: 'Draw',   desc: 'Paint pixels',        shortcut: 'B',
    svg: i('<path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>') },
  { type: 'erase',  label: 'Erase',  desc: 'Erase to black',      shortcut: 'E',
    svg: i('<path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/>') },
  { type: 'fill',   label: 'Fill',   desc: 'Flood fill',          shortcut: 'G',
    svg: i('<path d="m19 11-8-8-8.5 8.5a5.5 5.5 0 0 0 7.78 7.78L19 11Z"/><path d="m5 2 5 5"/><path d="M2 13h15"/><path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>') },
  { type: 'pick',   label: 'Pick',   desc: 'Eyedropper - sample a pixel', shortcut: 'I',
    svg: i('<path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/>') },
  { type: 'select', label: 'Select', desc: 'Rectangular select',  shortcut: 'S',
    svg: i('<rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="4 2"/>') },
  // Move tool removed from panel - drag inside selection auto-moves
];

const RAW_SHAPE_TOOLS: { type: ToolType; label: string; svg: string }[] = [
  { type: 'line',     label: 'Line',     svg: i('<line x1="5" y1="19" x2="19" y2="5"/>') },
  { type: 'rect',     label: 'Rect',     svg: i('<rect x="3" y="3" width="18" height="18" rx="1"/>') },
  { type: 'circle',   label: 'Circle',   svg: i('<circle cx="12" cy="12" r="9"/>') },
  { type: 'ellipse',  label: 'Ellipse',  svg: i('<ellipse cx="12" cy="12" rx="10" ry="6"/>') },
  { type: 'triangle', label: 'Triangle', svg: i('<path d="M12 2 2 22h20L12 2Z"/>') },
];

const RAW_SYMMETRY: { value: SymmetryMode; label: string; svg: string }[] = [
  { value: 'none',         label: 'None',
    svg: i('<line x1="4" y1="4" x2="20" y2="20" stroke-dasharray="2 2"/><line x1="20" y1="4" x2="4" y2="20" stroke-dasharray="2 2"/>') },
  { value: 'vertical',     label: 'Vert',
    svg: i('<line x1="12" y1="2" x2="12" y2="22"/><path d="M4 8 8 12 4 16"/><path d="M20 8 16 12 20 16"/>') },
  { value: 'horizontal',   label: 'Horiz',
    svg: i('<line x1="2" y1="12" x2="22" y2="12"/><path d="M8 4 12 8 16 4"/><path d="M8 20 12 16 16 20"/>') },
  { value: 'quad',         label: 'Quad',
    svg: i('<line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/>') },
  { value: 'diagonal-fwd', label: 'Diag /',
    svg: i('<line x1="4" y1="20" x2="20" y2="4"/>') },
  { value: 'diagonal-bk',  label: 'Diag \\',
    svg: i('<line x1="4" y1="4" x2="20" y2="20"/>') },
  { value: 'radial-3',     label: '3×',
    svg: i('<circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="10"/><line x1="3.8" y1="17.5" x2="10.4" y2="13.3"/><line x1="20.2" y1="17.5" x2="13.6" y2="13.3"/>') },
  { value: 'radial-5',     label: '5×',
    svg: i('<circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="10"/><line x1="3.4" y1="8.9" x2="10.2" y2="11.4"/><line x1="6.7" y1="20.1" x2="10.9" y2="13.7"/><line x1="17.3" y1="20.1" x2="13.1" y2="13.7"/><line x1="20.6" y1="8.9" x2="13.8" y2="11.4"/>') },
  { value: 'radial-6',     label: '6×',
    svg: i('<circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="10"/><line x1="20.7" y1="17" x2="13.7" y2="13"/><line x1="3.3" y1="17" x2="10.3" y2="13"/><line x1="12" y1="22" x2="12" y2="14"/><line x1="3.3" y1="7" x2="10.3" y2="11"/><line x1="20.7" y1="7" x2="13.7" y2="11"/>') },
  { value: 'radial-8',     label: '8×',
    svg: i('<circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="10"/><line x1="22" y1="12" x2="14" y2="12"/><line x1="12" y1="22" x2="12" y2="14"/><line x1="2" y1="12" x2="10" y2="12"/><line x1="19" y1="5" x2="14" y2="10"/><line x1="5" y1="19" x2="10" y2="14"/><line x1="19" y1="19" x2="14" y2="14"/><line x1="5" y1="5" x2="10" y2="10"/>') },
  { value: 'radial-12',    label: '12×',
    svg: i('<circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="9.5"/><line x1="17.5" y1="3.4" x2="14.2" y2="10"/><line x1="20.6" y1="6.9" x2="15.3" y2="10.7"/><line x1="21.8" y1="12" x2="14.5" y2="12"/><line x1="20.6" y1="17.1" x2="15.3" y2="13.3"/><line x1="17.5" y1="20.6" x2="14.2" y2="14"/><line x1="12" y1="22" x2="12" y2="14.5"/><line x1="6.5" y1="20.6" x2="9.8" y2="14"/><line x1="3.4" y1="17.1" x2="8.7" y2="13.3"/><line x1="2.2" y1="12" x2="9.5" y2="12"/><line x1="3.4" y1="6.9" x2="8.7" y2="10.7"/><line x1="6.5" y1="3.4" x2="9.8" y2="10"/>') },
  { value: 'star-5',       label: 'Star5',
    svg: i('<polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>') },
  { value: 'star-6',       label: 'Star6',
    svg: i('<path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6Z"/>') },
  { value: 'star-8',       label: 'Star8',
    svg: i('<path d="M12 2l1.8 5.5 5.5-1.8-3.7 4.3 3.7 4.3-5.5-1.8L12 22l-1.8-5.5-5.5 1.8 3.7-4.3-3.7-4.3 5.5 1.8Z"/>') },
];

@Component({
  selector: 'app-tools',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule, PxlNumberStepperDirective],
  templateUrl: './tools.html',
  styleUrls: ['./tools.css']
})
export class Tools implements OnInit, OnDestroy {
  activeTool$!: Observable<ToolType>;
  activeColor$!: Observable<string>;
  recentColors$!: Observable<string[]>;
  paletteColors$!: Observable<string[]>;
  brushSize$!: Observable<number>;
  symmetry$!: Observable<SymmetryMode>;
  shapeFill$!: Observable<ShapeFillMode>;
  isShapeTool$!: Observable<boolean>;
  isSpectator$!: Observable<boolean>;

  brushSizes = [1, 2, 3, 4, 6, 8];
  exactShapeW = 10;
  exactShapeH = 10;
  exactShapeR = 8;
  @Output() placeShape = new EventEmitter<{tool:string;w:number;h:number;r:number}>();

  // Typed after sanitization in ngOnInit
  drawTools:    { type: ToolType; label: string; desc: string; shortcut: string; svg: SafeHtml }[] = [];
  shapeTools:   { type: ToolType; label: string; svg: SafeHtml }[] = [];
  symmetryModes: { value: SymmetryMode; label: string; svg: SafeHtml }[] = [];

  private destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>, private san: DomSanitizer, private collabSvc: CollaborationService) {}

  ngOnInit(): void {
    // Sanitize SVGs now that DomSanitizer is injected
    this.drawTools    = RAW_DRAW_TOOLS.map(t => ({ ...t, svg: this.san.bypassSecurityTrustHtml(t.svg) }));
    this.shapeTools   = RAW_SHAPE_TOOLS.map(t => ({ ...t, svg: this.san.bypassSecurityTrustHtml(t.svg) }));
    this.symmetryModes = RAW_SYMMETRY.map(t => ({ ...t, svg: this.san.bypassSecurityTrustHtml(t.svg) }));

    this.activeTool$  = this.store.select(selectActiveTool);
    this.activeColor$ = this.store.select(selectActiveColor);
    this.recentColors$= this.store.select(selectRecentColors);
    this.brushSize$   = this.store.select(selectBrushSize);
    this.symmetry$    = this.store.select(selectSymmetryMode);
    this.shapeFill$   = this.store.select(selectShapeFillMode);
    this.isShapeTool$ = this.activeTool$.pipe(
      map(t => ['line','rect','circle','ellipse','triangle','polygon'].includes(t))
    );
    this.isSpectator$ = this.collabSvc.role$.pipe(map(role => role === 'spectator'));
    this.paletteColors$ = this.store.select(selectSettings).pipe(
      map(s => {
        const id = s.activePaletteId ?? 'dawnbringer16';
        const count = s.paletteColorCount ?? 16;
        const builtin = BUILTIN_PALETTES.find(p => p.id === id);
        if (builtin) return builtin.colors.slice(0, count);
        const custom = (s.customPalettes ?? []).find(p => p.id === id);
        return custom ? custom.colors.slice(0, count) : BUILTIN_PALETTES[0].colors.slice(0, 16);
      })
    );
  }

  placeExactShape(): void {
    this.activeTool$.subscribe(t => {
      this.placeShape.emit({ tool: t, w: this.exactShapeW, h: this.exactShapeH, r: this.exactShapeR });
    }).unsubscribe();
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  selectTool(t: ToolType): void {
    const role = this.collabSvc.role$.value;
    if (role === 'spectator') return;
    // Bug-5 fix: in draw-only collab mode, members may only use basic drawing tools.
    // Shape, fill, and selection tools go beyond pixel-painting and are blocked.
    if (this.collabSvc.isInRoom$.value && this.collabSvc.collabSettings$.value.membersDrawOnly && role === 'member') {
      const drawOnlyAllowed: ToolType[] = ['draw', 'erase', 'pick'];
      if (!drawOnlyAllowed.includes(t)) return;
    }
    this.store.dispatch(ToolActions.selectTool({ tool: t }));
  }
  setBrushSize(s: number): void    { if (this.collabSvc.role$.value === 'spectator') return; this.store.dispatch(ToolActions.setBrushSize({ size: s })); }
  setSymmetry(m: SymmetryMode): void  { if (this.collabSvc.role$.value === 'spectator') return; this.store.dispatch(ToolActions.setSymmetry({ mode: m })); }
  setShapeFill(m: ShapeFillMode): void { if (this.collabSvc.role$.value === 'spectator') return; this.store.dispatch(ToolActions.setShapeFill({ mode: m })); }

  onColorInput(e: Event): void {
    if (this.collabSvc.role$.value === 'spectator') return;
    const c = (e.target as HTMLInputElement).value;
    this.store.dispatch(ToolActions.setActiveColor({ color: c }));
    this.store.dispatch(ToolActions.addRecentColor({ color: c }));
  }
  selectColor(c: string): void {
    if (this.collabSvc.role$.value === 'spectator') return;
    this.store.dispatch(ToolActions.setActiveColor({ color: c }));
    this.store.dispatch(ToolActions.addRecentColor({ color: c }));
  }
}
